/**
 * Angular matrix parameters ("addClient;id=<base64>;type=1") are part of the URL contract: bookmarks, other
 * tabs and the base64 id scheme depend on them. React Router cannot match a partial segment, so screens that
 * take matrix params are mounted on ONE dynamic segment and dispatched by name (see MatrixSwitch).
 * The encoding below is Angular's own (encodeUriSegment) so the URLs are byte-identical.
 */

function encodeUriString(s: string): string {
  return encodeURIComponent(s).replace(/%40/g, '@').replace(/%3A/gi, ':').replace(/%24/g, '$').replace(/%2C/gi, ',');
}

/** Angular's encodeUriSegment: used for path segments and matrix keys/values. */
export function encodeUriSegment(s: string): string {
  return encodeUriString(s).replace(/\(/g, '%28').replace(/\)/g, '%29').replace(/%26/gi, '&');
}

/** Angular's encodeUriQuery: used for query keys/values. */
export function encodeUriQuery(s: string): string {
  return encodeUriString(s).replace(/%3B/gi, ';');
}

export type MatrixParams = Record<string, string | number | boolean | null | undefined>;

/** `buildMatrixSegment('addClient', { id: 'abc=' })` -> `addClient;id=abc%3D`. Null/undefined values are dropped. */
export function buildMatrixSegment(name: string, params: MatrixParams = {}): string {
  const serialised = Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${encodeUriSegment(key)}=${encodeUriSegment(String(value))}`);
  return [encodeUriSegment(name), ...serialised].join(';');
}

/** Full path, e.g. `matrixPath('/home/manage', 'addClient', { id })`. */
export function matrixPath(base: string, name: string, params: MatrixParams = {}): string {
  return `${base.replace(/\/+$/, '')}/${buildMatrixSegment(name, params)}`;
}

export interface ParsedMatrixSegment {
  name: string;
  params: Record<string, string>;
}

/**
 * Splits an (already URL-decoded by the router) segment such as `addClient;id=YWJj=;type=1`.
 * The value keeps everything after the FIRST '=' so base64 padding survives.
 */
export function parseMatrixSegment(segment: string | undefined): ParsedMatrixSegment {
  const [name = '', ...pairs] = (segment ?? '').split(';');
  const params: Record<string, string> = {};
  for (const pair of pairs) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    if (eq < 0) {
      params[pair] = '';
    } else {
      params[pair.slice(0, eq)] = pair.slice(eq + 1);
    }
  }
  return { name, params };
}

/** Query string in Angular's serialisation: `?returnUrl=%2Fhome%2Fx`. Empty -> ''. */
export function buildQuery(params: Record<string, string | undefined | null>): string {
  const parts = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeUriQuery(key)}=${encodeUriQuery(String(value))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}
